import type { MessageSubject } from '@/types/generated/public_api'

export const SUBSCRIPTION_REFRESH_COOLDOWN_MS = 2_000
export const SUBSCRIPTION_REFRESH_DELAY_MS = 50

type Channel = Partial<MessageSubject>
type TimerHandle = ReturnType<typeof setTimeout>

interface SubscriptionRefreshCoordinatorOptions {
  isActive: (channel: Channel) => boolean
  isReady: () => boolean
  send: (channels: Channel[], subscribe: boolean) => void
  now?: () => number
  schedule?: (callback: () => void, delayMs: number) => TimerHandle
  cancel?: (timer: TimerHandle) => void
}

/**
 * Sends a real wire unsubscribe/subscribe pair without touching the provider's
 * subscription ref-counts. Requests for the same channel are coalesced globally.
 */
export function createSubscriptionRefreshCoordinator({
  isActive,
  isReady,
  send,
  now = Date.now,
  schedule = setTimeout,
  cancel = clearTimeout,
}: SubscriptionRefreshCoordinatorOptions) {
  const lastRefreshAtByKey = new Map<string, number>()
  const pendingKeys = new Set<string>()
  const timers = new Set<TimerHandle>()

  const refresh = (channels: Channel[]): number => {
    if (!isReady()) return 0

    const requestedAt = now()
    const refreshable: Channel[] = []
    const seen = new Set<string>()

    for (const channel of channels) {
      const key = JSON.stringify(channel)
      if (seen.has(key) || pendingKeys.has(key) || !isActive(channel)) continue
      seen.add(key)

      const lastRefreshAt = lastRefreshAtByKey.get(key) ?? -Infinity
      if (requestedAt - lastRefreshAt < SUBSCRIPTION_REFRESH_COOLDOWN_MS) continue

      lastRefreshAtByKey.set(key, requestedAt)
      pendingKeys.add(key)
      refreshable.push(channel)
    }

    if (refreshable.length === 0) return 0

    send(refreshable, false)
    const timer = schedule(() => {
      timers.delete(timer)
      const activeChannels = refreshable.filter((channel) => {
        const key = JSON.stringify(channel)
        pendingKeys.delete(key)
        return isActive(channel)
      })
      if (activeChannels.length > 0 && isReady()) send(activeChannels, true)
    }, SUBSCRIPTION_REFRESH_DELAY_MS)
    timers.add(timer)

    return refreshable.length
  }

  const dispose = () => {
    timers.forEach(cancel)
    timers.clear()
    pendingKeys.clear()
    lastRefreshAtByKey.clear()
  }

  return { refresh, dispose }
}
